import { reactive, readonly } from 'vue';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  /** Auto-dismiss after this many ms. 0 = sticky. Default 4000. */
  durationMs: number;
}

export interface ToastInput {
  message: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastState {
  items: Toast[];
}

// Module-level singleton — shared across all useToast() callers in the app.
const state = reactive<ToastState>({ items: [] });
let nextId = 1;

function dismiss(id: number) {
  const idx = state.items.findIndex((t) => t.id === id);
  if (idx >= 0) state.items.splice(idx, 1);
}

function push(input: ToastInput): number {
  const id = nextId++;
  const toast: Toast = {
    id,
    message: input.message,
    variant: input.variant ?? 'info',
    durationMs: input.durationMs ?? 4000,
  };
  state.items.push(toast);
  if (toast.durationMs > 0) {
    setTimeout(() => dismiss(id), toast.durationMs);
  }
  return id;
}

export function useToast() {
  return {
    items: readonly(state).items,
    push,
    success: (message: string, opts?: Omit<ToastInput, 'message' | 'variant'>) =>
      push({ ...opts, message, variant: 'success' }),
    error: (message: string, opts?: Omit<ToastInput, 'message' | 'variant'>) =>
      push({ ...opts, message, variant: 'error' }),
    warning: (message: string, opts?: Omit<ToastInput, 'message' | 'variant'>) =>
      push({ ...opts, message, variant: 'warning' }),
    info: (message: string, opts?: Omit<ToastInput, 'message' | 'variant'>) =>
      push({ ...opts, message, variant: 'info' }),
    dismiss,
  };
}
